import React, { FC, memo, useCallback, useState } from 'react'
import styled, { css } from 'styled-components'
import Image from 'next/image'

import LoaderGrey from '../../assets/gifs/loader-grey.gif'

// Need to redeclare these types as `next/image` does not export these.
declare const VALID_LAYOUT_VALUES: readonly ['fill', 'fixed', 'intrinsic', 'responsive', undefined]
declare type LayoutValue = typeof VALID_LAYOUT_VALUES[number]

interface Props {
    src: string
    alt: string
    objectFit?: 'fill' | 'contain' | 'cover' | 'none' | 'scale-down'
    priority?: boolean
    quality?: number | string
    showPlaceholder?: boolean
    containerComponent?: React.ElementType
    layout?: LayoutValue
    width?: number | string
    height?: number | string
    onLoadingComplete?: () => void
    imwidth?: number
}

const NextImage: FC<Props> = ({
    src,
    alt,
    objectFit,
    priority = false,
    quality = 75,
    showPlaceholder = false,
    containerComponent,
    layout,
    width,
    height,
    onLoadingComplete,
    imwidth,
}) => {
    const [hasPlaceholder, setHasPlaceholder] = useState(true)

    const ImageContainer = containerComponent || Container

    const handleImageLoadingComplete = useCallback(() => {
        if (showPlaceholder) {
            setHasPlaceholder(false)
        }

        onLoadingComplete?.()
    }, [showPlaceholder, onLoadingComplete])

    const imageUrl = src && imwidth ? `${src}?imwidth=${imwidth}` : src

    return (
        <ImageContainer>
            {showPlaceholder && hasPlaceholder && (
                <Image src={LoaderGrey} alt="placeholder" layout="fill" objectFit="contain" />
            )}

            <Image
                src={imageUrl}
                alt={alt}
                layout={layout ?? 'fill'}
                objectFit={!!objectFit ? objectFit : 'cover'}
                onLoadingComplete={handleImageLoadingComplete}
                priority={priority}
                quality={quality}
                width={width}
                height={height}
            />
        </ImageContainer>
    )
}

const Container = styled.div(
    ({ theme }) => css`
        position: relative;
        overflow: hidden;
        height: ${theme.layout.fill};
        width: ${theme.layout.fill};
    `,
)

export default memo(NextImage)
